<template>
	<button
		:class="[
			$style['base-icon-button'],
			{
				[$style['base-icon-button--white']]: white,
				[$style['base-icon-button--primary']]: primary,
				[$style['base-icon-button--disabled']]: disabled,
				[$style['base-icon-button--loading']]: loading
			}
		]"
		@click="event => !disabled && $emit('click', event)"
	>
		<BaseRipple v-if="withRipple || white || primary" />
		<span v-show="!loading" :class="$style['base-icon-button__content']">
			<slot />
		</span>
	</button>
</template>

<script>
export default {
	name: 'BaseIconButton',
	emits: ['click'],
	props: {
		withRipple: { type: [Boolean, String], default: false },
		white: { type: [Boolean, String], default: false },
		loading: { type: [Boolean, String], default: false },
		primary: { type: [Boolean, String], default: false },
		disabled: { type: [Boolean, String], default: false }
	}
}
</script>

<style lang="scss" module>
.base-icon-button {
    z-index: 1;
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    appearance: none;
    padding: 0;
    margin: 0;
    border: 0;
    background-color: transparent;
    color: inherit;
    outline: 0;
    user-select: none;
    font-size: 14px;
    height: 24px;
    min-width: 24px;
    max-width: 24px;

    &:focus,
    &:active {
        outline: 0;
    }

    &::before {
        overflow: hidden;
        content: '';
        height: 40px;
        min-width: 40px;
        width: 40px;
        background-color: transparent;
        color: inherit;
        position: absolute;
        border-radius: 100%;
        z-index: -1;
        transition: 0.2s ease;
    }

    &:hover {
        cursor: pointer;

        &::before {
            background-color: getColor(hover-overlay-strong);
        }
    }

    svg {
        width: 100% !important;
    }

    &__content {
        display: flex;
        align-items: center;
        justify-content: center;
    }

    &--loading {
        color: transparent !important;
        pointer-events: none;

        &::before {
            content: '';
            position: absolute;
            animation: spin 0.5s infinite linear;
            border: 2px solid getColor(border);
            border-radius: 100%;
            border-right-color: transparent;
            border-top-color: transparent;
            display: block;
            height: 24px;
            width: 24px;
            min-width: 24px;
            min-height: 24px;
        }
    }

    // ------------------------------------------------------------
    // WHITE (secondary icon button)
    // ------------------------------------------------------------
    &--white {
        background-color: getColor(button-white);
        color: getColor(text);
        border: 1px solid transparent;
        box-shadow: 0 2px 6px 0 getColor(shadow-light);
        height: 40px;
        width: 40px;
        min-width: 40px;
        max-width: initial;
        border-radius: 100%;
        transition:
            background-color 0.15s ease,
            box-shadow 0.15s ease,
            color 0.15s ease;
        overflow: hidden;
        transform: translateZ(0);

        // Suppress the base ::before overlay; white variant handles hover via
        // its own background-color transition and the z-index:-1 pseudo-element
        // causes a subpixel dark-ring artifact at border-radius edges.
        &::before {
            content: none;
        }

        &:hover {
            cursor: pointer;
            background-color: getColor(surface-elevated-high);
            box-shadow: 0 3px 8px 0 getColor(shadow-light);
        }

        &.base-icon-button--loading {
            &::before {
                content: '';
                border-color: getColor(primary);
                border-right-color: transparent;
                border-top-color: transparent;
            }
        }

        &:active {
            background-color: getColor(surface-active);
            box-shadow: 0 1px 3px 0 getColor(shadow-light);
        }
    }

    // ------------------------------------------------------------
    // PRIMARY (filled icon button)
    // ------------------------------------------------------------
    &--primary {
        background-color: getColor(primary-button);
        color: getColor(white);
        border: 1px solid transparent;
        box-shadow: 0 2px 6px 0 getColor(shadow-light);
        height: 40px;
        width: 40px;
        min-width: 40px;
        max-width: initial;
        border-radius: 100%;
        transition:
            background-color 0.15s ease,
            box-shadow 0.15s ease,
            color 0.15s ease;
        overflow: hidden;
        transform: translateZ(0);

        // Suppress the base ::before overlay; primary variant handles hover via
        // its own background-color transition.
        &::before {
            content: none;
        }

        &:hover {
            cursor: pointer;
            background-color: getColor(primary-button-hover);
            box-shadow: 0 3px 8px 0 getColor(shadow-light);
        }

        &:active {
            background-color: getColor(primary-active);
            box-shadow: 0 1px 3px 0 getColor(shadow-light);
        }
    }

    // ------------------------------------------------------------
    // DARK MODE ONLY: borders for definition on dark surfaces
    // ------------------------------------------------------------
    @include dark-mode {
        &--white {
            border: 1px solid getColor(border-minor);
        }

        &--primary {
            border: 1px solid color-mix(in srgb, #{getColor(primary)} 25%, transparent);
        }

        &--disabled {
            border: 1px solid rgb(255, 255, 255, 0.06);
        }
    }

    &--disabled {
        cursor: not-allowed !important;
        color: getColor(disabled-link) !important;
        background-color: getColor(button-disabled) !important;
        box-shadow: none !important;
        border-radius: 100%;
    }
}

@keyframes spin {
    from {
        transform: rotate(0);
    }

    to {
        transform: rotate(359deg);
    }
}
</style>
